import {Injectable} from '@angular/core';
import {AngularFirestore, AngularFirestoreCollection, CollectionReference} from '@angular/fire/compat/firestore';
import {BehaviorSubject, catchError, delay, forkJoin, map, mergeMap, Observable, of} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AngularFireStorage} from '@angular/fire/compat/storage';
import {serverTimestamp} from 'firebase/firestore';
import {getStorage, ref, uploadBytes} from 'firebase/storage';
import {AuthService} from './auth.service';
import {ToastrService} from 'ngx-toastr';

export enum SignalTypes {
    Empty = 'empty',
    Action = 'action',
    Article = 'article',
    NoAction = 'noAction',
    ImageSet = 'imageSet',
    End = 'end',
}

@Injectable({
    providedIn: 'root'
})
export class DataService {
    CSessions: AngularFirestoreCollection<any>;
    // tslint:disable-next-line:variable-name
    _loaded = 0;
    $latestResultId: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    orderIdKey = 'orderId';

    constructor(
        private http: HttpClient,
        private firestore: AngularFirestore,
        private auth: AuthService,
        private storage: AngularFireStorage,
        private toastr: ToastrService
    ) {
        this.CSessions = firestore.collection('sessions');

        // this.auth.updateUserState.subscribe((res) => {
        //   if (res) {
        //     this.getUser()
        //       .subscribe();
        //   }
        // });
        this.auth.updateUserState.subscribe((res) => {
            if (res) {
                console.log('getJournal: ', res);
                this.getJournal();
            }
        });
    }

    get serverTimestamp() {
        return serverTimestamp();
    }

    get sid(): any {
        return localStorage.getItem('sid');
    }

    set sid(prop: any) {
        localStorage.setItem('sid', prop);
    }

    get loaded() {
        return this._loaded;
    }

    set loaded(prop) {
        this._loaded = prop;
    }

    getQuestionnaire(apiUrl: string, testId: string) {
        return this.getData(`${apiUrl}/storage/algorithm/${testId}`)
            .pipe(
                mergeMap((algorithms: any) => {
                    const formRequests: any = [];
                    const entryPoint = algorithms['@entrypoint'];
                    Object.keys(algorithms.nodes).forEach(key => {
                        const splittedKey = key.split(':');
                        if (splittedKey[0] === 'form') {
                            formRequests.push(
                                this.getData(`${apiUrl}/storage/form/${splittedKey[1]}`)
                                    .pipe(
                                        map((form: any) => {
                                            form.id = key;
                                            return form;
                                        })
                                    )
                            );
                        }
                    });
                    if (formRequests.length) {
                        return forkJoin(formRequests)
                            .pipe(
                                map((forms: any) => {
                                    const questionnaire: any = [];
                                    Object.keys(algorithms.nodes)
                                        .forEach((key: any) => {
                                            const node = algorithms.nodes[key];
                                            if (node['@type'] === 'form') {
                                                node.questions = forms.find((el: any) => el.id === key);
                                                questionnaire.push(this.formItem(key, node));
                                            }
                                            if (node['@type'] === 'question') {
                                                questionnaire.push(this.questionItem(key, node));
                                            }
                                            if (node['@type'] === 'message') {
                                                questionnaire.push(this.messageItem(key, node));
                                            }
                                        });
                                    return questionnaire;
                                }),
                                catchError(() => {
                                    return of('Oops!');
                                }),

                                map((questionnaire) => {
                                    return {
                                        questionnaire,
                                        entryPoint
                                    };
                                })
                            );
                    } else {
                        const questionnaire: any = [];
                        Object.keys(algorithms.nodes)
                            .forEach((key: any) => {
                                const node = algorithms.nodes[key];
                                if (node['@type'] === 'question') {
                                    questionnaire.push(this.questionItem(key, node));
                                }
                                if (node['@type'] === 'message') {
                                    questionnaire.push(this.messageItem(key, node));
                                }
                                return of({
                                    questionnaire: [{id: entryPoint['@entryPoint'], end: true}],
                                    entryPoint
                                });
                            });
                        return of(
                            {
                                questionnaire,
                                entryPoint
                            }
                        );
                    }
                })
            );
    }

    private formItem(key: any, el: any) {
        return {
            id: key,
            label: el.questions.description,
            type: 'test',
            targetId: el.link['@ref'],
            end: false,
            questions: el.questions.items.map((question: any) => {
                if (question['@type'] === 'message') {
                    return this.textItem(question);
                }
                if (question?.input[0]['@type'] === 'input') {
                    return this.textItem(question);
                }
                return this.radioButtonItem(question);
            })
        };
    }

    private textItem(question: any) {
        return {
            key: question['@index'],
            type: 'textarea',
            className: 'label',
            templateOptions: {
                label: question.label,
                rows: 5,
                required: false,
            },
            // template: '<div class="text-info">' + question.label + '</div>',
        };
    }

    private radioButtonItem(question: any) {
        return {
            key: question['@index'],
            type: 'radio',
            wrappers: ['radio'],
            className: question.type === 'bar' ? 'bar horizontal-radio' : 'horizontal-radio',
            defaultValue: null,
            templateOptions: {
                label: question.label,
                options: question.input[0].option.map((option: any) => {
                    return {
                        label: option['#text'],
                        value: option['@value']
                    };
                }),
                required: true,
                type: question.type === 'date' ? question.type === 'date' : null,
            }
        };
    }

    private questionItem(key: any, el: any) {
        return {
            id: key,
            label: null,
            type: 'question',
            targetId: null,
            end: false,
            questions: [
                {
                    key,
                    type: 'radio',
                    className: 'horizontal-radio',
                    templateOptions: {
                        label: el.label,
                        options: el.link.map((option: any) => {
                            return {
                                label: option['#text'],
                                value: option['@ref']
                            };
                        }),
                        required: true,
                        type: null,
                    }
                }
            ]
        };
    }

    private messageItem(key: any, el: any) {
        return {
            id: key,
            label: el.label,
            type: 'test',
            targetId: el?.link && el?.link['@ref'] ? el?.link['@ref'] : null,
            end: !el?.link,
            questions: [],
        };
    }

    getScenario(url: string, testId: string) {
        return this.http.get(`${url}/storage/scenario/${testId}`);
    }

    getData(url: string) {
        return this.http.get(url);
    }

    getOptions(type: number): any[] {
        switch (type) {
            case 1:
                return [
                    {
                        label: 'ніколи',
                        value: 1
                    },
                    {
                        label: 'декілька днів',
                        value: 2
                    },
                    {
                        label: 'більшу половину часу',
                        value: 3
                    },
                    {
                        label: 'майже щодня',
                        value: 4
                    }
                ];
            default:
                return [];
        }
    }

    createSession(apiUrl: string, uid: string, testId: any, putag?: any): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`,
        });
        const data = {
            uid,
            createdAt: this.serverTimestamp,
            test: testId,
            putag
        };
        return this.http.post(`${apiUrl}/session`, data, {
            headers,
            responseType: 'text'
        })
            .pipe(
                delay(1000)
            );
    }

    subscribeUser(apiUrl: string): Observable<any> {

        return this.http.post(`${apiUrl}/subscribe`, null, {
            headers: {
                Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`,
            },
            responseType: 'text'
        });
    }

    getPlans(apiUrl: string, id: any): Observable<any> {
        return this.http.get(`${apiUrl}/storage/planset/${id}`);
    }

    closeSession(apiUrl: string, sid: any) {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`,
        });
        return this.http.post(`${apiUrl}/session/${sid}/close`, null, {
            headers,
            responseType: 'text'
        })
            .pipe(
                catchError((err) => {
                    this.toastr.error(err.statusText, 'Error');
                    throw err;
                })
            );
    }

    getSession(id: any): Observable<any> {
        return this.CSessions.doc(id).get()
            .pipe(
                map((doc) => {
                    if (doc.exists) { // if document exists ...
                        const docid = doc.id;
                        const data = doc.data() as any;
                        return {id: docid, ...data}; // ... return id and data.
                    } else { // if document does not exist ...
                        throw new Error('No such document!'); // ... throw an Error.
                    }
                }),
                catchError((err) => {
                    this.toastr.error(err);
                    throw err;
                })
            );
    }

    saveFormResult(uid: string, sid: any, source: string, data: any) {
        return this.firestore.collection('forms')
            .add({
                uid: uid.toString(),
                sid: sid.toString(),
                answers: data
            });
    }

    uploadBlob(path: string, blob: Blob) {
        return uploadBytes(ref(getStorage(), path), blob);
    }

    getResultsState(sid: any): Observable<any> {
        const uid = this.auth.userData?.uid;
        return this.firestore.collection('users').doc(uid).collection('journal').doc(sid).valueChanges();
    }

    getJournal() {
        const uid = this.auth.userData?.uid;
        return this.firestore.collection('users')
            .doc(uid)
            .collection('journal', (r: CollectionReference) => r.orderBy('recordedAt', 'desc').limit(1)).valueChanges({idField: 'id'})
            .subscribe((res) => {
                if (res[0]) {
                    this.$latestResultId.next(res[0]?.id);
                }
            });
    }

    getSessions() {
        const uid = this.auth.userData?.uid;
        return this.firestore.collection('users')
            .doc(uid).collection('journal', (r: CollectionReference) => r.orderBy('recordedAt', 'desc'))
            .valueChanges({idField: 'id'});
    }

    getResults(apiUrl: string, sid: any): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`,
        });
        return this.http.get(`${apiUrl}/report/${sid}`, {
            headers
        })
            .pipe(
                catchError((err) => {
                    throw err;
                })
            );
    }

    unlockResults(apiUrl: string, sid: any): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`,
        });
        return this.http.post(`${apiUrl}/report/${sid}/unlock`, null, {
            headers
        })
            .pipe(
                catchError((err) => {
                    throw err;
                })
            );
    }

    saveEmail(url: string, email: string) {
        return this.http.post(`${url}/emails/?email=${email}`, null)
            .pipe(
                map(() => {
                    this.toastr.success('We will send you updates as soon as possible', 'Nice!');
                }),
                catchError((err: any) => {
                    this.toastr.error('Something goes wrong', 'Oops');
                    return err;
                })
            );
    }

    generateLiqPayLink(uid: string, orderId: string, pid: string, type: 'authorize' | 'capture', url: string, returnUrl: string) {
        return `${url}/orders?uid=${uid}&orderid=${orderId}&package=${pid}&type=${type}&redirectUrl=${returnUrl}`;
    }

    generateUniqId() {
        const date = new Date().getTime();
        const s = Math.floor(date / 1000).toString(16).toUpperCase();
        const partOne = s.slice(0, s.length / 2).trim();
        const partTwo = s.slice(s.length / 2, s.length).trim();
        return `${partTwo}${partOne}`;
    }

    checkLiqPayStatus(url: string, ORDERID: any) {
        return this.http.get(`${url}/orders/${ORDERID}/status`);
    }

    checkLiqPaySubscriptionStatus(uid: string) {
        return this.firestore.collection('subscriptions').doc(uid).snapshotChanges();
    }

    get orderId(): string | null {
        return localStorage.getItem(this.orderIdKey);
    }

    set orderId(prop: string | null) {
        prop ? localStorage.setItem(this.orderIdKey, prop) : localStorage.removeItem(this.orderIdKey);
    }

    get orderType(): string | null {
        return localStorage.getItem('orderType');
    }

    set orderType(prop: string | null) {
        prop ? localStorage.setItem('orderType', prop) : localStorage.removeItem('orderType');
    }
}
